/**
 * PLDV-8941 — content that must stay hidden from logged-out visitors.
 *
 * The primary criterion is the SUPPLIER, not the provider. Relax is also an
 * aggregator: ~40 third-party studios ship through `provider: 'relax'` (see
 * `features/gameIntegration/config/relaxSuppliers.ts`) and must stay visible,
 * while Relax Gaming's own titles also arrive through Patrianna
 * (`provider: 'patrianna'`, game codes prefixed `rlx_`) and must be hidden.
 * A provider-level rule gets both of those wrong.
 *
 * Supplier fields (`supplierSearchCode`, `supplierTitle`) are present on every
 * game payload, including the `GameProductShort` swimlane items from
 * `/v1/product-layout` and `/v1/product-category`, so this predicate covers all
 * surfaces directly.
 */

export const DISABLED_PROVIDERS: readonly string[] = []

/**
 * Supplier-level exclusions, matched against `supplierSearchCode`, `supplier`
 * and `supplierTitle` (trimmed, lowercased — see `normalizeSupplierValue`).
 *
 * Every spelling variant has to be listed explicitly. Separators are never
 * normalised away, because `peterandsons` and `fantasma-games` are distinct
 * non-Relax suppliers that would otherwise collide with `peter and sons` and
 * `fantasma games (silver bullet)`.
 *
 * Whether Relax-network partner studios (Avatar UX, Print Studios, 4ThePlayer,
 * ReelPlay, Peter & Sons, …) also belong here is still open with the business
 * side. If confirmed, extend this list with their exact spellings.
 */
export const DISABLED_SUPPLIERS: readonly string[] = ['relax-gaming', 'relax gaming']

/**
 * Provider entities hidden from the /providers page and unreachable by deep link,
 * matched against `Provider.code` (which is also the `[providerName]` route param).
 *
 * This must stay explicit. Filtering a provider's games is NOT enough to remove its
 * tile: `providersListFormatting` keeps any provider with 5 or more products, and
 * `relax-gaming` may keep enough non-Relax-supplied games to stay above that bar.
 */
export const DISABLED_PROVIDER_CODES: readonly string[] = ['relax-gaming']

export const HAS_DISABLED_CONTENT =
  DISABLED_PROVIDERS.length > 0 || DISABLED_SUPPLIERS.length > 0 || DISABLED_PROVIDER_CODES.length > 0
